if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupMobileSwipe();
        this.updatePhotoRatio(this.querySelector('.product__media-item.is-active') || this.querySelector('.product__media-item'));
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      updatePhotoRatio(item) {
        const img=item?.querySelector('img');
        if(!img)return;
        const update=()=>this.style.setProperty('--sp-active-ratio',(img.naturalWidth || Number(img.getAttribute('width')))/(img.naturalHeight || Number(img.getAttribute('height'))));
        update();img.addEventListener('load',update,{once:true});
      }

      onSlideChanged(event) {
        if (this.closest('.sp-product') && this.mql.matches) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setupMobileSwipe() {
        if (!this.closest('.sp-product')) return;
        const rail = this.elements.viewer?.querySelector('.product__media-list');
        if (!rail) return;
        // The rail scrolls and snaps natively, so the finger drags the image itself.
        // This only mirrors the resting slide back into the thumbnails.
        let idle = null;
        const sync = () => {
          if (this.mql.matches || this.railIsAnimating) return;
          const slides = [...rail.children].filter((item) => item.dataset.mediaId);
          if (!slides.length) return;
          const nearest = slides.reduce(
            (best, item) =>
              Math.abs(item.offsetLeft - rail.scrollLeft) < Math.abs(best.offsetLeft - rail.scrollLeft) ? item : best,
            slides[0]
          );
          if (nearest.classList.contains('is-active')) return;
          slides.forEach((item) => item.classList.remove('is-active'));
          nearest.classList.add('is-active');
          this.updatePhotoRatio(nearest);
          if (!this.elements.thumbnails) return;
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${nearest.dataset.mediaId}"]`);
          if (!thumbnail) return;
          this.setActiveThumbnail(thumbnail);
          this.announceLiveRegion(nearest, thumbnail.dataset.mediaPosition);
        };
        rail.addEventListener(
          'scroll',
          () => {
            clearTimeout(idle);
            idle = setTimeout(sync, 90);
          },
          { passive: true }
        );
        rail.addEventListener('scrollend', sync, { passive: true });
      }

      setActiveMedia(mediaId, prepend, keepPagePosition = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.updatePhotoRatio(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            // Guard the sync listener against the scroll this call causes itself.
            this.railIsAnimating = true;
            clearTimeout(this.railSettle);
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            });
            this.railSettle = setTimeout(() => {
              this.railIsAnimating = false;
            }, 450);
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (keepPagePosition || activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.closest('.sp-product') && this.mql.matches) {
          const rail = this.elements.thumbnails.querySelector('.thumbnail-list');
          const itemRect = thumbnail.getBoundingClientRect();
          const railRect = rail.getBoundingClientRect();
          if (itemRect.top < railRect.top || itemRect.bottom > railRect.bottom) {
            rail.scrollTo({top: rail.scrollTop + itemRect.top - railRect.top, behavior: 'smooth'});
          }
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
